import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react'

export type SnackbarType = 'success' | 'error' | 'info'

interface SnackbarProps {
  message: string
  isOpen: boolean
  onClose: () => void
  type?: SnackbarType
  duration?: number
}

const Snackbar: React.FC<SnackbarProps> = ({
  message,
  isOpen,
  onClose,
  type = 'info',
  duration
}) => {
  // Error messages stay longer (5s), others 3s
  const resolvedDuration = duration ?? (type === 'error' ? 5000 : 3000)

  useEffect(() => {
    if (isOpen && resolvedDuration > 0) {
      const timer = setTimeout(() => {
        onClose()
      }, resolvedDuration)
      return () => clearTimeout(timer)
    }
  }, [isOpen, resolvedDuration, onClose])

  const config = {
    success: {
      icon: <CheckCircle className="w-5 h-5" style={{ color: 'var(--status-success-text)' }} />,
      iconBg: 'var(--status-success-bg)',
      title: 'Listo',
      accentColor: 'var(--status-success-text)',
    },
    error: {
      icon: <AlertCircle className="w-5 h-5" style={{ color: 'var(--status-error-text)' }} />,
      iconBg: 'var(--status-error-bg)',
      title: 'Error',
      accentColor: 'var(--status-error-text)',
    },
    info: {
      icon: <Info className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />,
      iconBg: 'color-mix(in srgb, var(--color-primary), transparent 85%)',
      title: 'Info',
      accentColor: 'var(--color-primary)',
    },
  }

  const { icon, iconBg, title, accentColor } = config[type]

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="fixed bottom-20 right-4 lg:bottom-4 z-[200] flex items-start gap-3 px-4 py-3.5 rounded-2xl shadow-xl bg-card border border-border-base min-w-[280px] max-w-sm"
          style={{
            borderLeftWidth: '3px',
            borderLeftColor: accentColor,
          }}
        >
          {/* Icon badge */}
          <div
            className="p-2 rounded-xl shrink-0 mt-0.5"
            style={{ backgroundColor: iconBg }}
          >
            {icon}
          </div>

          {/* Content */}
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: accentColor }}>
              {title}
            </p>
            <p className="text-sm font-medium text-text-main leading-snug">{message}</p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-text-main/10 rounded-full transition-colors shrink-0 mt-0.5"
          >
            <X className="w-3.5 h-3.5 text-text-secondary" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default Snackbar
